import { Primary, SelectField } from '@cinch-labs/design-system'
import { dashify, formatNumber } from '@cinch-labs/shared-util'
import React, { useEffect, useRef, useState } from 'react'
import { getVehiclesDataByCondition } from '../../../data/get-vehicles-data-by-condition'

import {
  Makes,
  VehicleCondition,
  VehiclesData,
} from '../../../types/vehicles-data'
import { Price } from '../filters/price/price'
import styles from './search-cars-form.module.css'
import { ctaClickEvent, TrackingValue } from './tracking'

export interface SearchCarsFormProps {
  vehicleCondition: VehicleCondition
  trackingValue: TrackingValue
  vehicleData: VehiclesData
  section?: string
}

interface FieldOption {
  value: string
  label: string
}

enum PriceFrequency {
  MonthlyPrice = 'monthly-price',
  TotalPrice = 'total-price',
}

interface PriceFrequencyOption {
  value: PriceFrequency
  label: string
}

const PRICE_FREQUENCY_OPTIONS: Readonly<PriceFrequencyOption[]> = [
  {
    value: PriceFrequency.MonthlyPrice,
    label: 'Monthly price',
  },
  {
    value: PriceFrequency.TotalPrice,
    label: 'Total price',
  },
]

const getTotalPriceVehicles = async (vehicleCondition: VehicleCondition) => {
  const { vehiclesData } = await getVehiclesDataByCondition(
    vehicleCondition,
    false,
  )
  return vehiclesData
}

export const SearchCarsForm: React.FC<SearchCarsFormProps> = ({
  vehicleCondition,
  vehicleData,
  section,
  trackingValue,
}) => {
  const { totalStock, priceBucket, monthlyPriceBucket, makes } = vehicleData

  const monthlyPriceVehicles = vehicleData
  const totalPriceVehicles = useRef<VehiclesData>()
  const getVehiclesDataByConditionAndPrice = async (
    vehicleCondition: VehicleCondition,
    priceFrequency: PriceFrequency,
  ) => {
    if (priceFrequency === PriceFrequency.MonthlyPrice) {
      return monthlyPriceVehicles
    } else {
      if (!totalPriceVehicles.current) {
        totalPriceVehicles.current = await getTotalPriceVehicles(
          vehicleCondition,
        )
      }
      return totalPriceVehicles.current
    }
  }

  const [makeOptions, setMakeOptions] = useState<FieldOption[]>(
    makes.map((make) => ({
      value: make.name,
      label: `${make.name} (${make.stock})`,
    })),
  )
  const [selectedMakeOption, setSelectedMakeOption] = useState<string>()

  const [modelOptions, setModelOptions] = useState<FieldOption[]>([])
  const [selectedModelOption, setSelectedModelOption] = useState<string>()

  const [selectedMaxTotalPrice, setSelectedMaxTotalPrice] = useState<number>(0)
  const [selectedPriceFrequencyOption, setSelectedPriceFrequencyOption] =
    useState<PriceFrequencyOption>(PRICE_FREQUENCY_OPTIONS[0])

  const [filteredMakes, setFilteredMakes] = useState<Makes[]>(makes)

  const [searchResultsCount, setSearchResultsCount] =
    useState<number>(totalStock)

  useEffect(() => {
    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )
    if (!selectedMake) return

    if (selectedMake.models?.length) {
      setModelOptions(
        selectedMake.models.map((model) => ({
          value: model.name,
          label: `${model.name} (${model.stock})`,
        })),
      )
    }

    setSearchResultsCount(selectedMake.stock)

    // reset the selected make option label to not display stock
    setMakeOptions(
      makeOptions.map((makeOption) => {
        return makeOption.value === selectedMakeOption
          ? { ...makeOption, label: makeOption.value }
          : makeOption
      }),
    )
  }, [selectedMakeOption])

  useEffect(() => {
    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )
    if (!selectedMake) return

    const selectedModel = selectedMake.models.find(
      (x) => x.name === selectedModelOption,
    )
    if (!selectedModel) return

    setSearchResultsCount(selectedModel.stock)

    // reset the selected model option label to not display stock
    setModelOptions(
      modelOptions.map((modelOption) => {
        return modelOption.value === selectedModelOption
          ? { ...modelOption, label: modelOption.value }
          : modelOption
      }),
    )
  }, [selectedModelOption])

  useEffect(() => {
    setMakeOptions(
      filteredMakes.map((make) => ({
        value: make.name,
        label:
          selectedMakeOption === make.name
            ? make.name
            : `${make.name} (${make.stock})`,
      })),
    )

    const selectedMake = filteredMakes.find(
      (x) => x.name === selectedMakeOption,
    )

    if (!selectedMake) {
      getVehiclesDataByConditionAndPrice(
        vehicleCondition,
        selectedPriceFrequencyOption.value,
      )
        .then((vehicleData) => setSearchResultsCount(vehicleData.totalStock))
        .catch((error) => {
          console.error('Unable to get total stock:', error)
        })

      return
    }

    if (selectedMake?.models?.length) {
      setModelOptions(
        selectedMake.models.map((model) => ({
          value: model.name,
          label:
            selectedModelOption === model.name
              ? model.name
              : `${model.name} (${model.stock})`,
        })),
      )
    }

    if (selectedModelOption) {
      const selectedModel = selectedMake?.models.find(
        (x) => x.name === selectedModelOption,
      )
      if (selectedModel) {
        setSearchResultsCount(selectedModel.stock)
      }
    } else if (selectedMakeOption) {
      setSearchResultsCount(selectedMake?.stock)
    }
  }, [filteredMakes])

  const onPriceFrequencyChange = (selectedOption: PriceFrequencyOption) => {
    getVehiclesDataByConditionAndPrice(vehicleCondition, selectedOption.value)
      .then((vehicleData) => setFilteredMakes(vehicleData.makes))
      .catch((error) => console.error('Unable to get vehicle data:', error))

    setSelectedPriceFrequencyOption(selectedOption)
  }

  const onSubmit = () => {
    const urlSegments = [
      `/${vehicleCondition}-cars`,
      ...(selectedMakeOption && selectedMakeOption !== 'any'
        ? [`${dashify(selectedMakeOption)}`]
        : []),
      ...(selectedModelOption && selectedModelOption !== 'any'
        ? [`${dashify(selectedModelOption)}`]
        : []),
    ]

    const urlParams = new URLSearchParams()
    if (selectedPriceFrequencyOption.value === PriceFrequency.MonthlyPrice) {
      urlParams.append('financeType', 'any')
    }

    const baseUrl = urlSegments.join('/')
    const redirectUrl =
      urlParams.toString().length > 0
        ? `${baseUrl}?${urlParams.toString()}`
        : baseUrl

    window.location.assign(redirectUrl)
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        ctaClickEvent(
          trackingValue,
          selectedMakeOption,
          selectedModelOption,
          selectedPriceFrequencyOption.value.toString(),
        )
        onSubmit()
      }}
    >
      <fieldset className={styles['radioGroup']}>
        {PRICE_FREQUENCY_OPTIONS.map((option) => (
          <div key={option.value} className={styles['radioInputContainer']}>
            <label htmlFor={option.value} className={styles['priceLabel']}>
              <input
                type="radio"
                id={option.value}
                name={option.value}
                checked={option.value === selectedPriceFrequencyOption.value}
                onChange={() => onPriceFrequencyChange(option)}
              />
              {option.label}
            </label>
          </div>
        ))}
      </fieldset>
      <Price
        radioChecked={selectedPriceFrequencyOption.value}
        handleOnChangeTracking={() => console.log('track')}
        maxTotalPrice={selectedMaxTotalPrice}
        setMaxTotalPrice={setSelectedMaxTotalPrice}
        priceBucket={priceBucket}
        monthlyPriceBucket={monthlyPriceBucket}
      />
      <SelectField
        label="Select make"
        labelHidden
        id="make"
        name="make"
        data-testid="make-select"
        options={makeOptions}
        placeholder={'Select make'}
        placeholderValue={'Any'}
        onChange={(event) => {
          const value = event.target.value
          // Only accept values that come from the known options list or
          // the explicit 'any' placeholder
          if (
            value === 'any' ||
            makeOptions.some((option) => option.value === value)
          ) {
            setSelectedMakeOption(value)
          }
        }}
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />
      <SelectField
        label="Select model"
        labelHidden
        id="model"
        name="model"
        data-testid="model-select"
        options={modelOptions}
        placeholder={'Select model'}
        placeholderValue={'Any'}
        onChange={(event) => {
          const value = event.target.value
          // Only accept values that come from the known options list or
          // the explicit 'any' placeholder
          if (
            value === 'any' ||
            modelOptions.some((option) => option.value === value)
          ) {
            setSelectedModelOption(value)
          }
        }}
        disabled={
          !selectedMakeOption ||
          selectedMakeOption.toLocaleLowerCase() === 'any'
        }
        error={{
          show: false,
          classNames: { container: styles['errorBox'] },
        }}
      />

      <div className={styles['buttonContainer']}>
        <Primary
          type="submit"
          classNames={{ button: styles['button'] }}
          data-testid="searchButton"
          isFullWidth
        >
          {searchResultsCount === 1
            ? 'Search 1 car'
            : `Search ${formatNumber(searchResultsCount)} cars`}
        </Primary>
      </div>
    </form>
  )
}
